import { JsonPatchOperation } from './jsonPatchUtils';
import { Project, ProjectUpdateRequest } from '../models/Project';

/**
 * Create patch operations specifically for Project entities
 * Handles date formatting and filters out non-patchable fields
 */
export const createProjectPatchOperations = (
  original: Partial<Project>,
  modified: ProjectUpdateRequest
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  // List of fields that can be patched on the Project entity
  const patchableFields: (keyof Project)[] = [
    'name',
    'domain',
    'missionType',
    'type',
    'companyId',
    'objectives',
    'presentationAndNeeds',
    'talentHardSkills',
    'talentSoftSkills',
    'budget',
    'dailyRate',
    'duration',
    'durationType',
    'fullTime',
    'daysPerPeriod',
    'workingDays',
    'location',
    'hybridDetails',
    'teamIntegration',
    'desiredStartDate',
    'startDate',
    'endDate',
    'feePercentage',
    'externalCostsFeePercentage',
    'expensesFeePercentage',
    'processingFees',
    'internalNotes'
  ];

  // Date fields that need special handling
  const dateFields = ['desiredStartDate', 'startDate', 'endDate'];

  // Compare each field
  patchableFields.forEach(field => {
    const originalValue = original[field];
    const modifiedValue = (modified as any)[field];

    // Skip if modified doesn't have this field
    if (modifiedValue === undefined) {
      return;
    }

    // Special handling for date fields
    if (dateFields.includes(field)) {
      // Normalize dates for comparison
      const normalizeDate = (value: any): string | null => {
        if (!value) return null;
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return null;
          
          // For comparison, only use the date part (YYYY-MM-DD)
          // This avoids issues with time zone differences
          return date.toISOString().split('T')[0];
        } catch {
          return null;
        }
      };

      const originalNormalized = normalizeDate(originalValue);
      const modifiedNormalized = normalizeDate(modifiedValue);

      // Only add operation if dates are actually different
      if (originalNormalized !== modifiedNormalized) {
        // When sending the patch, send the full ISO string
        const valueToSend = modifiedValue ? new Date(modifiedValue).toISOString() : null;
        operations.push({
          op: 'replace',
          path: `/${field}`,
          value: valueToSend
        });
      }
    }
    // Handle arrays
    else if (Array.isArray(modifiedValue)) {
      if (!Array.isArray(originalValue) ||
          JSON.stringify(originalValue) !== JSON.stringify(modifiedValue)) {
        operations.push({
          op: 'replace',
          path: `/${field}`,
          value: modifiedValue
        });
      }
    }
    // Handle null values
    else if (modifiedValue === null) {
      if (originalValue !== null) {
        operations.push({
          op: 'replace',
          path: `/${field}`,
          value: null
        });
      }
    }
    // Handle boolean values
    else if (typeof modifiedValue === 'boolean') {
      if (originalValue !== modifiedValue) {
        operations.push({
          op: 'replace',
          path: `/${field}`,
          value: modifiedValue
        });
      }
    }
    // Handle numbers (including 0)
    else if (typeof modifiedValue === 'number') {
      if (originalValue !== modifiedValue) {
        operations.push({
          op: 'replace',
          path: `/${field}`,
          value: modifiedValue
        });
      }
    }
    // Handle strings and other primitive values
    else if (originalValue !== modifiedValue) {
      operations.push({
        op: 'replace',
        path: `/${field}`,
        value: modifiedValue
      });
    }
  });

  return operations;
};

/**
 * Extract non-patchable fields from ProjectUpdateRequest
 * These fields need to be handled separately (not via PATCH)
 */
export const extractNonPatchableFields = (data: ProjectUpdateRequest) => {
  const { status, ...patchableFields } = data;
  return {
    patchableFields,
    nonPatchableFields: {
      status
    }
  };
};